import { Fragment, PureComponent, ReactNode } from 'react';
import {
    components,
    OptionProps,
    ValueContainerProps,
    ControlProps,
    MenuProps,
    StylesConfig,
    MenuPlacement,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { CSSObject } from '@emotion/serialize';
import fetchDoctorAutoSuggest from '../api/fetchDoctorAutoSuggest';
import { LS_NEW_PT_DF_AS } from '../pages/utils';
import Fuse from 'fuse.js';

type SuggestionItem = { id?: string; name?: string; number?: string; speciality?: string } & {
    __isNew__?: boolean;
    __api__?: boolean;
};

const Option = (props: OptionProps<SuggestionItem, false>) => {
    const { data } = props;

    return (
        <components.Option {...props}>
            <div className="flex items-center justify-between font-600 text-neutral-3">
                {data.__isNew__ ? (
                    <p className="text-actionPrimary">+ Add doctor {data?.name || ''}</p>
                ) : (
                    <Fragment>
                        <div className="flex items-center justify-center space-x-8">
                            <div>
                                <img
                                    src={`https://a.eka.care/doctor-avatar/${data?.id}`}
                                    className="h-28 w-28 object-cover rounded-full overflow-hidden"
                                    alt=""
                                />
                            </div>

                            <div>
                                <div className="flex space-x-8">
                                    <p className="text-neutral-1 text-14">{data?.name}</p>
                                    {data?.id && (
                                        <span className="top-1 right-1 relative mr-3 w-16 h-16 mr-8">
                                            <svg
                                                className="w-16 h-16"
                                                viewBox="0 0 105 117"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M104.436 33.768C104.144 38.8643 102.1 43.6607 98.4497 47.408L63.4061 83.3816C62.53 84.2809 61.5079 85.1803 60.3398 85.9297C52.3089 91.4757 41.3578 90.4264 34.4951 83.3816C30.2607 79.0348 28.0704 72.8893 28.6545 66.5939C28.8005 62.8467 31.8668 59.9988 35.5172 59.9988C39.3136 59.9988 42.3799 63.1464 42.3799 67.0436C42.3799 69.142 41.5038 70.9407 40.1897 72.2897C40.6277 73.4889 41.3578 74.688 42.3799 75.5873C44.1321 77.386 46.6143 78.4352 49.0966 78.4352C50.7028 78.4352 52.1629 78.1354 53.4771 77.386C54.3531 76.9363 55.0832 76.3368 55.8133 75.5873L73.0431 57.9003L65.8883 50.5557L55.6673 40.0634C53.9151 38.2647 51.4328 37.2155 48.9506 37.2155C46.4683 37.2155 43.9861 38.2647 42.2339 40.0634C41.0658 41.2625 40.1897 42.7614 39.7516 44.2603C41.3578 45.6093 42.5259 47.5579 42.5259 49.8062C42.5259 53.7034 39.4596 56.8511 35.6632 56.8511C32.1589 56.8511 29.0925 54.0032 28.8005 50.4058C27.6324 43.6607 29.6766 36.9157 34.4951 31.9693C38.2915 28.0722 43.402 25.8238 48.9506 25.8238C54.3531 25.8238 59.6097 28.0722 63.4061 31.9693L73.6271 42.4616L80.7819 49.8062L90.7109 39.6137C92.6091 37.6651 93.4852 35.2669 93.4852 32.5689C93.4852 30.1706 92.6091 27.9223 91.0029 26.2735C86.7685 21.477 81.658 17.7298 75.9634 15.0318C69.9768 12.3337 63.6981 10.8348 57.1274 10.8348C44.8622 10.8348 33.473 15.7812 24.7121 24.6247C16.0972 33.4682 11.2787 45.3095 11.2787 57.9003C11.2787 70.4911 16.0972 82.1825 24.7121 91.1759C33.327 100.019 44.8622 104.966 57.1274 104.966C63.6981 104.966 69.9768 103.617 75.9634 100.769C81.658 98.0708 86.7685 94.3236 91.0029 89.5271C94.3613 85.6299 94.2152 79.7842 90.7109 76.037L83.4101 68.5425L91.1489 60.5983L98.4497 68.0928C106.188 76.037 106.481 88.6277 99.1798 97.0216C93.9232 103.017 87.6446 107.664 80.6359 110.961C73.3351 114.409 65.4503 116.058 57.2734 116.058C49.6807 116.058 42.2339 114.559 35.2252 111.561C28.5085 108.563 22.3758 104.366 17.2653 99.12C12.0088 93.724 7.92036 87.5785 5.14608 80.6836C2.22578 73.4889 0.765625 65.8445 0.765625 58.0502C0.765625 50.2559 2.22578 42.6115 5.14608 35.4168C8.06638 28.5219 12.1548 22.2265 17.2653 16.9803C22.5219 11.5843 28.5085 7.38737 35.2252 4.53946C42.2339 1.54166 49.6807 0.042762 57.2734 0.042762C65.4503 0.042762 73.1891 1.84143 80.6359 5.13901C87.7906 8.43659 93.9232 13.0832 99.1798 19.0788C101.37 21.6269 103.122 24.7746 103.852 28.0722C104.29 29.4212 104.436 31.6695 104.436 33.768Z"
                                                    fill="#7265E3"
                                                ></path>
                                            </svg>
                                        </span>
                                    )}
                                </div>

                                {data?.number && (
                                    <div className="uppercase text-11 text-darwin-neutral-600">
                                        {[data?.speciality, data?.number]
                                            .filter(Boolean)
                                            .join(' | ')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Fragment>
                )}
            </div>
        </components.Option>
    );
};

const ValueContainer = (props: ValueContainerProps<SuggestionItem, false>) => {
    const { hasValue, children, getValue, setValue, selectProps } = props;
    const { isDisabled, isModal } = selectProps;

    if (hasValue) {
        const value: SuggestionItem = getValue()[0];

        return isModal ? (
            <div className={`relative flex items-center justify-between gap-8 py-10 px-12`}>
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        width="11"
                        height="15"
                        viewBox="0 0 11 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.5625 0.75C9.51953 0.75 10.3125 1.54297 10.3125 2.5V13C10.3125 13.9844 9.51953 14.75 8.5625 14.75H2.4375C1.45312 14.75 0.6875 13.9844 0.6875 13V2.5C0.6875 1.54297 1.45312 0.75 2.4375 0.75H8.5625ZM9 13V2.5C9 2.28125 8.78125 2.0625 8.5625 2.0625H2.4375C2.19141 2.0625 2 2.28125 2 2.5V13C2 13.2461 2.19141 13.4375 2.4375 13.4375H8.5625C8.78125 13.4375 9 13.2461 9 13ZM6.375 11.6875C6.59375 11.6875 6.8125 11.9062 6.8125 12.125C6.8125 12.3711 6.59375 12.5625 6.375 12.5625H4.625C4.37891 12.5625 4.1875 12.3711 4.1875 12.125C4.1875 11.9062 4.37891 11.6875 4.625 11.6875H6.375Z"
                            fill="#3E445C"
                        />
                    </svg>
                </div>
                <input
                    type="text"
                    value={''}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full"
                    disabled={isDisabled}
                />
                <div className="mimic-box"></div>
            </div>
        ) : (
            <div className="add-pt-input">
                <input
                    type="text"
                    value={''}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                    disabled={isDisabled}
                />
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        className="w-16 h-16 fill-neutral-5"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                    >
                        <path d="M272 304h-96C78.8 304 0 382.8 0 480c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32C448 382.8 369.2 304 272 304zM48.99 464C56.89 400.9 110.8 352 176 352h96c65.16 0 119.1 48.95 127 112H48.99zM224 256c70.69 0 128-57.31 128-128c0-70.69-57.31-128-128-128S96 57.31 96 128C96 198.7 153.3 256 224 256zM224 48c44.11 0 80 35.89 80 80c0 44.11-35.89 80-80 80S144 172.1 144 128C144 83.89 179.9 48 224 48z"></path>
                    </svg>
                </div>
                <div className="mimic-box"></div>
            </div>
        );
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<SuggestionItem, false>) => {
    const { children } = props;
    return <Fragment>{children}</Fragment>;
};

const Menu = (props: MenuProps<SuggestionItem, false>) => {
    const { children, options } = props;
    if (options.length === 0) {
        return null;
    }

    return <components.Menu {...props}>{children}</components.Menu>;
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { selectProps, onChange, onFocus, value } = props;
    const { placeholder, onBlur, isDisabled, isModal } = selectProps;

    const onCChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(e);
    };

    return isModal ? (
        <div className={`relative flex items-center justify-between gap-8 py-10 px-12`}>
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    width="11"
                    height="15"
                    viewBox="0 0 11 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M8.5625 0.75C9.51953 0.75 10.3125 1.54297 10.3125 2.5V13C10.3125 13.9844 9.51953 14.75 8.5625 14.75H2.4375C1.45312 14.75 0.6875 13.9844 0.6875 13V2.5C0.6875 1.54297 1.45312 0.75 2.4375 0.75H8.5625ZM9 13V2.5C9 2.28125 8.78125 2.0625 8.5625 2.0625H2.4375C2.19141 2.0625 2 2.28125 2 2.5V13C2 13.2461 2.19141 13.4375 2.4375 13.4375H8.5625C8.78125 13.4375 9 13.2461 9 13ZM6.375 11.6875C6.59375 11.6875 6.8125 11.9062 6.8125 12.125C6.8125 12.3711 6.59375 12.5625 6.375 12.5625H4.625C4.37891 12.5625 4.1875 12.3711 4.1875 12.125C4.1875 11.9062 4.37891 11.6875 4.625 11.6875H6.375Z"
                        fill="#3E445C"
                    />
                </svg>
            </div>
            <input
                type="text"
                className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full bg-darwin-neutral-50"
                placeholder={placeholder}
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={isDisabled}
            />{' '}
            <div className="mimic-box"></div>
        </div>
    ) : (
        <div className={`add-pt-input`}>
            <input
                type="text"
                className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                placeholder={placeholder}
                value={value}
                onBlur={onBlur}
                onChange={onCChange}
                disabled={isDisabled}
            />{' '}
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    className="w-16 h-16 fill-neutral-5"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                >
                    <path d="M272 304h-96C78.8 304 0 382.8 0 480c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32C448 382.8 369.2 304 272 304zM48.99 464C56.89 400.9 110.8 352 176 352h96c65.16 0 119.1 48.95 127 112H48.99zM224 256c70.69 0 128-57.31 128-128c0-70.69-57.31-128-128-128S96 57.31 96 128C96 198.7 153.3 256 224 256zM224 48c44.11 0 80 35.89 80 80c0 44.11-35.89 80-80 80S144 172.1 144 128C144 83.89 179.9 48 224 48z"></path>
                </svg>
            </div>
            <div className="mimic-box"></div>
        </div>
    );
};

const DropdownIndicator = () => null;
const NoOptionsMessage = () => null;
const LoadingIndicator = () => null;
const ClearIndicator = () => null;
const IndicatorsContainer = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Option,
    NoOptionsMessage,
    LoadingIndicator,
    Menu,
    ClearIndicator,
    IndicatorsContainer,
};

const customStyles: StylesConfig<SuggestionItem, false> = {
    menu: (base) => {
        return {
            ...base,
            paddingTop: 0,
        } as CSSObject;
    },
    option: (base, state) =>
        ({
            ...base,
            backgroundColor: state.isFocused ? '#f4f6f9' : 'white',
        } as CSSObject),
};

type Props = {
    inputValue: string;
    onSelect: (p?: SuggestionItem) => void;
    onInputChange: (text: string) => void;
    disabled: boolean;
    suggestionsEnabled?: boolean;
    isModal?: boolean;
    onBlur?: any;
    autoFocus?: boolean;
    className?: string;
    menuPlacement?: MenuPlacement;
};

type State = {
    suggestedPatients: SuggestionItem[];
    menuIsOpen: boolean;
};

const fuseConfig = { keys: ['name', 'number'], threshold: 0.2 };

class PatientReferralAutoSuggest extends PureComponent<Props, State> {
    fuse = new Fuse<SuggestionItem>([], fuseConfig);

    state: State = {
        suggestedPatients: [],
        menuIsOpen: false,
    };

    loadReferralSuggestions = () => {
        if (localStorage?.getItem(LS_NEW_PT_DF_AS)) {
            console.time('debugtimeone');
            const localStorageData = JSON.parse(localStorage?.getItem(LS_NEW_PT_DF_AS) || '{}');
            console.timeEnd('debugtimeone');

            console.time('debugtimetwo');
            const data = localStorageData?.['referred-by-name-and-numberv3'];

            console.timeEnd('debugtimetwo');

            console.time('debugtimethree');

            console.timeEnd('debugtimethree');

            this.fuse = new Fuse<SuggestionItem>(data, fuseConfig);
        }
    };

    componentDidMount() {
        this.loadReferralSuggestions();
    }

    onNameChange = (input: string): void => {
        this.setState({
            menuIsOpen: true,
        });

        const { disabled } = this.props;

        if (disabled) {
            return;
        }

        this.props.onInputChange(input);
    };

    onChange = (patient: SuggestionItem | null): void => {
        const { onSelect } = this.props;

        if (patient === null) {
            onSelect();
            return;
        }

        onSelect(patient);
    };

    onReferralBlur = (): void => {
        const { inputValue } = this.props;
        this.setState({ menuIsOpen: false });
        this.props.onBlur && this.props.onBlur();
    };

    loadPatients = async (doctorName: string): Promise<SuggestionItem[]> => {
        const doctorSuggestions = await fetchDoctorAutoSuggest(doctorName);

        const results = new Array<SuggestionItem>();

        const localStorageData = this.fuse?.search(doctorName).map((option) => option.item) || [];

        results.push(...localStorageData?.slice(0, 3));

        const data =
            doctorSuggestions?.data?.map((suggestion) => ({
                id: suggestion.oid,
                name: suggestion.name,
                speciality: suggestion.major_specialisation,
                __api__: true,
            })) || [];

        results.push(...data);
        const finalResults = results.slice(0, 4);

        if (doctorName?.length > 3) {
            finalResults.push({
                name: doctorName,
                __isNew__: true,
            });
        }

        return finalResults;
    };

    private readonly filterOption = () => true;

    render(): ReactNode {
        const { menuIsOpen } = this.state;
        const { inputValue, disabled, className, menuPlacement } = this.props;

        const children = (
            <AsyncSelect
                value={[]}
                isClearable={true}
                components={customComponents}
                menuPlacement={menuPlacement || 'top'}
                styles={customStyles}
                loadOptions={this.loadPatients}
                onInputChange={this.onNameChange}
                inputValue={inputValue}
                onChange={this.onChange}
                onBlur={this.onReferralBlur}
                filterOption={this.filterOption}
                menuIsOpen={menuIsOpen}
                tabSelectsValue={false}
                isDisabled={disabled}
                className={className || 'w-50p'}
                placeholder={`Referred By Doctor's Name`}
            />
        );

        return (
            <div className={`space-y-4 ${this.props.className ?? ''}`}>
                <div className="flex justify-between">
                    <p className="text-neutral-3 text-14 font-700">Referred By Doctor's Name</p>
                </div>
                {children}
            </div>
        );
    }
}

export default PatientReferralAutoSuggest;
